import { Button, ButtonProps, Toast } from "@vesatogo/grass-core"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"
import * as XLSX from "xlsx"

type DownloadExcelButtonProps = Omit<ButtonProps, "onClick" | "loading"> & {
  getData: () => Promise<Record<string, any>[]>
  fileName?: string
  sheetName?: string
  columnWidths?: number[]
  amountColumns?: string[]
  text?: string
}

const DownloadExcelButton = ({
  getData,
  fileName,
  sheetName = "Sheet1",
  columnWidths,
  amountColumns,
  text = "Download Excel",
  ...buttonProps
}: DownloadExcelButtonProps) => {
  const [loading, setLoading] = useState(false)

  const handleDownload = async () => {
    if (loading) return
    try {
      setLoading(true)
      toast.custom(
        <Toast title="Preparing Excel download..." id="excel-download" />,
        { duration: 2000 }
      )
      const rows = await getData()
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(rows)

      if (amountColumns?.length) {
        const range = XLSX.utils.decode_range(ws["!ref"] || "A1")
        for (let c = range.s.c; c <= range.e.c; c++) {
          const headerCell = ws[XLSX.utils.encode_cell({ r: 0, c })]
          if (amountColumns.includes(headerCell?.v)) {
            for (let r = range.s.r + 1; r <= range.e.r; r++) {
              const cell = ws[XLSX.utils.encode_cell({ r, c })]
              if (cell && typeof cell.v === "number") {
                cell.z = "#,##0.00"
              }
            }
          }
        }
      }

      if (columnWidths?.length) {
        ws["!cols"] = columnWidths.map(wch => ({ wch }))
      }

      XLSX.utils.book_append_sheet(wb, ws, sheetName)
      const fallbackName = `export-${dayjs().format("YYYY-MM-DD-HHmmss")}.xlsx`
      const finalName = fileName || fallbackName

      const sheetData = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      })
      const blob = new Blob([sheetData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = finalName
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.custom(
        <Toast title="Excel downloaded successfully!" id="excel-download" />,
        { duration: 2000 }
      )
    } catch (err) {
      console.log("excel download error", err)
      toast.custom(
        <Toast
          intent="danger"
          title={
            err instanceof Error ? err.message : "Failed to download Excel!"
          }
          id="excel-download"
        />,
        { duration: 2000 }
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      text={text}
      loading={loading}
      onClick={handleDownload}
      {...buttonProps}
    />
  )
}

export default DownloadExcelButton
